<template>
    <div class="container mx-auto px-0 py-2">
        <div class="flex justify-between items-center mb-6">
            <div>
                <h1 class="text-3xl font-bold text-white tracking-tight">Tag Types</h1>
                <p class="mt-2 text-white/80">Manage and organize your tag types</p>
            </div>
            <button 
                @click="createTagType" 
                class="btn rounded-full text-white bg-echoes-primary hover:bg-echoes-primary/80 border-transparent"
            >
                <i class="mdi mdi-plus"></i> New Tag Type
            </button>
        </div>

        <!-- Search and Filter Section -->
        <div class="mb-6 space-y-4">
            <!-- Search Bar -->
            <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="mdi mdi-magnify text-gray-400"></i>
                </div>
                <input
                    type="text"
                    v-model="searchQuery"
                    placeholder="Search tag types..."
                    class="block w-full pl-10 pr-3 py-2 border border-echoes-border-gray rounded-lg
                           bg-echoes-background-black text-white
                           placeholder-white/50
                           focus:outline-none focus:border-echoes-primary"
                />
            </div>
        </div>

        <!-- Modal de Criação/Edição -->
        <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
            <div class="flex items-center justify-center min-h-screen px-4">
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>
                
                <div class="relative bg-echoes-background-black rounded-lg shadow-xl max-w-md w-full">
                    <div class="flex justify-between items-center px-6 py-4 border-b border-echoes-border-gray">
                        <h2 class="text-xl font-semibold text-white">
                            {{ isEditing ? 'Edit Tag Type' : 'New Tag Type' }}
                        </h2>
                        <button @click="closeModal" class="text-white/80 bg-transparent hover:bg-echoes-background-gray/80 hover:text-white rounded-lg text-sm p-1.5 ms-auto inline-flex items-center">
                            <svg class="size-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                            </svg>
                        </button>
                    </div>

                    <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
                        <div>
                            <div class="flex gap-2 items-center mb-1">
                                <label for="tagType" class="block text-sm font-medium text-white">
                                    Tag Type
                                </label>
                            </div>
                            <TextInput id="tagType" v-model="newTagType" placeholder="Enter custom tag type" />
                        </div>

                        <div>
                            <label for="tagTypeDescription" class="block text-sm font-medium text-white mb-1">
                                Description
                            </label>
                            <TextInput v-model="newTagTypeDescription" placeholder="Enter tag type description" type="textarea" rows="3" />
                        </div>

                        <div class="flex justify-end gap-3 mt-6">
                            <button 
                                type="button"
                                @click="closeModal"
                                class="btn rounded-full bg-echoes-background-gray text-echoes-text-gray hover:bg-echoes-background-gray/80 border-transparent px-4 py-1.5 text-sm"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                class="btn rounded-full text-white bg-echoes-primary hover:bg-echoes-primary/80 border-transparent px-4 py-1.5 text-sm"
                            >
                                {{ isEditing ? 'Save Changes' : 'Create Tag Type' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Tabela de Tag Types -->
        <div class="bg-echoes-background-black rounded-lg shadow overflow-hidden w-full">
            <div class="max-h-[600px] overflow-y-auto w-full">
                <BaseTable :columnDefs="columns" :rowData="rowData" :quickFilterText="searchQuery" :useFitGridWidth="true" :rowSelection="true" :rowHeight="50" :tableHeight="600" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import BaseTable from '@/components/table/BaseTable.vue';
import { useToast } from '@/composables/use-toast';
import axios from '@/plugins/axios';

const tagTypesOptions = ref([]);
const showModal = ref(false);
const newTagType = ref('');
const newTagTypeDescription = ref('');
const rowData = ref([]);
const searchQuery = ref('');
const isEditing = ref(false);
const editingTagId = ref(null);
const isCustomType = ref(false);

import { useLayoutComponents } from '@/composables/layout.js';

const { addToast } = useToast();

const { TextInput } = useLayoutComponents();

onMounted(async () => {
    await fetchTagTypes();
    await fetchTagTypesOptions();
});

const columns = [
    {
        headerName: 'Type',
        field: 'type',
        type: 'text',
        flex: 4,
        minWidth: 200,
        maxWidth: 500,
        suppressSizeToFit: false,
        cellStyle: {
            paddingRight: 0,
        },
    },
    {
        headerName: 'Description',
        field: 'description',
        flex: 8,
        minWidth: 400,
        maxWidth: 800,
        suppressSizeToFit: false,
        type: 'text',
        tooltipValueGetter: (params) => params.data.description_tooltip,
    },
    {
        headerName: 'Actions',
        field: 'actions',
        flex: 3,
        minWidth: 250,
        maxWidth: 300,
        suppressSizeToFit: false,
        cellRenderer: (params) => {
            const container = document.createElement('div');
            container.className = 'flex items-center gap-2';
            
            // Edit Button
            const editBtn = document.createElement('button');
            editBtn.className = 'flex items-center gap-2 px-2.5 text-gray-100 rounded-md h-7 bg-violet-500 hover:bg-violet-600';
            editBtn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg> Edit`;
            editBtn.onclick = () => editTagType(params.data);
            
            // Delete Button
            const deleteBtn = document.createElement('button');
            deleteBtn.className = 'flex items-center gap-2 px-2.5 text-gray-100 rounded-md h-7 bg-red-500 hover:bg-red-600';
            deleteBtn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>Delete`;
            deleteBtn.onclick = () => deleteTagType(params.data);
            
            container.appendChild(editBtn);
            container.appendChild(deleteBtn);
            return container;
        },
    }
];

const fetchTagTypes = async () => {
    try {
        const response = await axios.get('/api/tag_types');
        if(response.status === 200) {
            rowData.value = response.data.map(tagType => ({
                id: tagType.id,
                type: tagType.type,
                description: tagType.description.substring(0, 100) + '...',
                description_tooltip: tagType.description,
            }));
        } else {
            console.error('Error fetching tags:', response.data);
        }
    } catch (error) {
        console.error('Error fetching tags:', error);
    }
};

const fetchTagTypesOptions = async () => {
    try {
        const response = await axios.get('/api/tag_types');
        if(response.status === 200) {
            tagTypesOptions.value = response.data.map(tagTypes => ({
                label: tagTypes.type,
                value: tagTypes.type
            }));
        } else {
            console.error('Error fetching tags:', response.data);
        }
    } catch (error) {
        console.error('Error fetching tags:', error);
    }
};

const createTagType = () => {
    isEditing.value = false;
    newTagType.value = '';
    newTagTypeDescription.value = '';
    showModal.value = true;
};

const editTagType = async (tagType) => {
    isEditing.value = true;
    editingTagId.value = tagType.id;
    newTagType.value = tagType.type;
    newTagTypeDescription.value = tagType.description;
    showModal.value = true;
};

const deleteTagType = async (tagType) => {
    try {
        if(confirm('Are you sure you want to delete this tag type?') && tagType?.id) {
            const response = await axios.post(`/api/tag_types/${tagType.id}/delete`);
            if(response.status === 200 || response.status === 201) {
                await fetchTagTypes();
                addToast('Tag Type Deleted','success');
            } else {
                addToast(response.data.message || 'An error occurred','error');
            }
        }
    } catch (error) {
        addToast(error.response?.data?.message || 'An error occurred','error');
    }
};

const closeModal = () => {
    showModal.value = false;
    newTagType.value = '';
    newTagTypeDescription.value = '';
    isEditing.value = false;
    editingTagId.value = null;
    isCustomType.value = false;
};

const handleSubmit = async () => {
    const url = isEditing.value ? `/api/tag_types/${editingTagId.value}` : '/api/tag_types';
    const payload = {
        type: newTagType.value,
        description: newTagTypeDescription.value
    };

    try {
        const response = await axios.post(url, payload);
        if (response.status === 201) {
            await fetchTagTypes();
            closeModal();
        } else {
            console.error('Error creating/updating tag:', response.data);
        }
    } catch (error) {
        console.error('Error creating/updating tag:', error);
    }
    closeModal();
};
</script>